// src/pages/DashboardPage.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useFeatures } from '../contexts/FeaturesContext';
import Toast from '../components/Toast';
import {
    Sun, Moon, ChevronDown, User, LogOut, Menu, Lock, Construction, X, Settings,
    Plus, LayoutGrid,
    Library, Bookmark, Shuffle, PenLine,
    BookMarked, Award,
    LayoutDashboard, GraduationCap, ClipboardList, BarChart3, Calendar, Users
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import InterdictedModal from '../components/AccessControl/InterdictedModal';
import UpgradePlanModal from '../components/AccessControl/UpgradePlanModal';

import { SidebarPage, StatsSubTab, ExploreSubTab } from '../components/dashboard/Sidebar';
import ExploreContent from '../components/dashboard/ExploreContent';
import Statistics from '../components/dashboard/Statistics';
import GroupsMainTab from '../components/dashboard/groups/GroupsMainTab';
import QuestionsPage from './QuestionsPage';
import MyNotebook from '../components/question/MyNotebook';
import QuestionManager from '../components/admin/questions/QuestionManager';
import ProvasSimulados from '../components/exam/ProvasSimulados';
import PlannerPage from '../components/planner/PlannerPage';
import Painel from '../components/dashboard/Painel';
import CreateFlashcardsPage from './CreateFlashcardsPage';

import { Cycle, DashboardStats, SavedCard, WeekHistory } from '../components/dashboard/types';
import './DashboardPage.css';

const API_BASE_URL = '/api';

const TAB_LABELS: Record<string, string> = {
    painel: 'Painel',
    explore: 'Flashcards',
    questions: 'Questões',
    provas: 'Provas e Simulados',
    stats: 'Estatísticas',
    planner: 'Assistente Graia',
    groups: 'Grupos de Estudo',
};

const SUBTAB_LABELS: Record<string, string> = {
    'explore.content': 'Campos de Estudo',
    'explore.saved': 'Flashcards Salvos',
    'explore.custom': 'Estudo Personalizado',
    'explore.create-flashcards': 'Criar Flashcards',
    'questions.study': 'Estudar',
    'questions.notebook': 'Meu Caderno',
    'stats.flashcards': 'Flashcards',
    'stats.questions': 'Questões',
};

const EXPLORE_SUBTABS: ExploreSubTab[] = ['content', 'saved', 'custom'];
const STATS_SUBTABS: StatsSubTab[] = ['flashcards', 'questions'];

const isBlocked = (
    map: Record<string, boolean> | undefined,
    key: string
): boolean => {
    if (!map) return false;
    if (key.includes('.')) {
        const parent = key.split('.')[0];
        if (map[parent]) return true;
    }
    return !!(map[key]);
};

const firstAvailableSubtab = <T extends string>(
    tabKey: string,
    subtabs: T[],
    map: Record<string, boolean>
): T | null => {
    return subtabs.find(st => !isBlocked(map, `${tabKey}.${st}`)) ?? null;
};

// ── Pills de seção ────────────────────────────────────────────
interface PillDef {
    id: string;
    label: string;
    Icon: React.ElementType;
    locked?: boolean;
    interdicted?: boolean;
}

const SectionPills: React.FC<{
    pills: PillDef[];
    active: string;
    onChange: (id: string) => void;
}> = ({ pills, active, onChange }) => (
    <div className="dash-pills-bar">
        <div className="dash-pills">
            {pills.map(p => (
                <button
                    key={p.id}
                    className={[
                        'dash-pill',
                        active === p.id ? 'active' : '',
                        (p.locked || p.interdicted) ? 'locked' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => onChange(p.id)}
                >
                    <p.Icon size={14} />
                    <span>{p.label}</span>
                    {p.interdicted && <Construction size={11} className="dash-pill-status" />}
                    {p.locked && !p.interdicted && <Lock size={11} className="dash-pill-status" />}
                </button>
            ))}
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────
const DashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAdmin, isVendor, logout } = useAuth();
    const { features, canAccess, loading: featuresLoading } = useFeatures();

    const statsFlashcardsOnly = !isAdmin && !!(features as any)?.statsFlashcardsOnly;
    const hasQuestions = isAdmin || (!featuresLoading && canAccess('questions'));
    const hasPlanner   = isAdmin || (!featuresLoading && canAccess('planner'));

    const [questionsViewMode,    setQuestionsViewMode]    = useState<'study' | 'notebook' | 'manage'>('study');
    const [questionsManageInitId, setQuestionsManageInitId] = useState<string | null>(null);
    const [activeStatsTab,       setActiveStatsTab]        = useState<StatsSubTab>('flashcards');
    const [activeProvasSection,  setActiveProvasSection]   = useState<string>('provas-catalogo');

    const [activePage, setActivePage] = useState<SidebarPage>(() => {
        const state = location.state as any;
        const page = state?.activePage;
        if (page === 'create-flashcards') return 'explore';
        return (page as SidebarPage) || 'painel';
    });

    const [activeExploreTab, setActiveExploreTab] = useState<ExploreSubTab>(() => {
        const state = location.state as any;
        if (state?.activePage === 'create-flashcards') return 'create-flashcards';
        return state?.activeExploreTab || 'content';
    });

    const [loading,         setLoading]         = useState(true);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [mobileNavOpen,   setMobileNavOpen]   = useState(false);

    const [cycles,           setCycles]           = useState<Cycle[]>([]);
    const [savedCards,       setSavedCards]       = useState<SavedCard[]>([]);
    const [detailedStats,    setDetailedStats]    = useState<DashboardStats | null>(null);
    const [weekHistory,      setWeekHistory]      = useState<WeekHistory[]>([]);
    const [isSavingOrder,    setIsSavingOrder]    = useState(false);
    const [hasRealPurchases, setHasRealPurchases] = useState(false);

    const { theme, toggleTheme } = useTheme();

    const [interdictedMap,   setInterdictedMap]   = useState<Record<string, boolean>>({});
    const [interdictedModal, setInterdictedModal] = useState<{ isOpen: boolean; name: string } | null>(null);
    const [upgradeModal,     setUpgradeModal]     = useState<{ open: boolean; featureName?: string }>({ open: false });

    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const showToast = (message: string, type: 'success' | 'error' | 'info') =>
        setToast({ message, type });

    // Limpar location.state após usar no initializer — evita que F5 restaure página anterior
    useEffect(() => {
        if (location.state) {
            navigate(location.pathname, { replace: true, state: null });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Travar scroll quando nav mobile está aberta
    useEffect(() => {
        if (mobileNavOpen) document.body.style.overflow = 'hidden';
        else               document.body.style.overflow = '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileNavOpen]);

    // Fechar nav mobile ao trocar de página
    useEffect(() => { setMobileNavOpen(false); }, [activePage]);

    // Resetar modo de questões ao sair da aba
    useEffect(() => {
        if (activePage !== 'questions') {
            setQuestionsViewMode('study');
            setQuestionsManageInitId(null);
        }
    }, [activePage]);

    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`${API_BASE_URL}/platform-config`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setInterdictedMap(data.interdictedTabs || {});
                }
            } catch (e) { console.error('Erro config:', e); }
        };
        load();
    }, []);

    const CACHE_TTL = 3 * 60 * 1000;

    const fromCache = (key: string) => {
        try {
            const raw = sessionStorage.getItem(key);
            if (!raw) return null;
            const { data, timestamp } = JSON.parse(raw);
            if (Date.now() - timestamp > CACHE_TTL) return null;
            return data;
        } catch { return null; }
    };

    const loadCycles = async (forceRefresh = false) => {
        if (!forceRefresh) {
            const cached = fromCache('dashboard_cycles');
            if (cached) {
                setCycles(cached.cycles || []);
                setHasRealPurchases(cached.hasRealPurchases || false);
                return;
            }
        }
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/dashboard/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setCycles(data.cycles || []);
                    setHasRealPurchases(data.hasRealPurchases || false);
                    sessionStorage.setItem('dashboard_cycles', JSON.stringify({
                        data: {
                            cycles: data.cycles,
                            hasRealPurchases: data.hasRealPurchases
                        },
                        timestamp: Date.now()
                    }));
                }
            }
        } catch { showToast('Erro ao carregar conteúdo', 'error'); }
    };

    const loadStats = async () => {
        const cachedStats = fromCache('dashboard_stats');
        const cachedWeek  = fromCache('dashboard_week');

        if (cachedStats) setDetailedStats(cachedStats);
        if (cachedWeek)  setWeekHistory(cachedWeek.history);
        if (cachedStats && cachedWeek) return;

        try {
            const token = localStorage.getItem('accessToken');
            const [sRes, hRes] = await Promise.all([
                cachedStats ? null : fetch(`${API_BASE_URL}/stats/dashboard`,     { headers: { Authorization: `Bearer ${token}` } }),
                cachedWeek  ? null : fetch(`${API_BASE_URL}/stats/week-history`,  { headers: { Authorization: `Bearer ${token}` } }),
            ]);
            if (sRes?.ok) {
                const d = await sRes.json();
                setDetailedStats(d);
                sessionStorage.setItem('dashboard_stats', JSON.stringify({ data: d, timestamp: Date.now() }));
            }
            if (hRes?.ok) {
                const d = await hRes.json();
                setWeekHistory(d.history);
                sessionStorage.setItem('dashboard_week', JSON.stringify({ data: d, timestamp: Date.now() }));
            }
        } catch { /* silencioso */ }
    };

    const loadSavedCards = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/saved-cards`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) setSavedCards((await res.json()).savedCards);
        } catch { /* silencioso */ }
    };

    const exploreCreateCycleRef       = useRef<(() => void) | null>(null);
    const exploreCreateAreaRef        = useRef<(() => void) | null>(null);
    const exploreManageAreasRef       = useRef<(() => void) | null>(null);
    const savedFoldersNewFolderRef    = useRef<(() => void) | null>(null);
    const createFlashcardsNewColRef   = useRef<(() => void) | null>(null);
    const notebookOpenGridRef         = useRef<(() => void) | null>(null);
    const contentAdminMenuRef         = useRef<HTMLDivElement>(null);

    const [showContentAdminMenu, setShowContentAdminMenu] = useState(false);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (contentAdminMenuRef.current && !contentAdminMenuRef.current.contains(e.target as Node)) {
                setShowContentAdminMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        const loadAll = async () => {
            const hasCachedCycles = !!fromCache('dashboard_cycles');
            const hasCachedStats  = !!fromCache('dashboard_stats');
            const hasCachedWeek   = !!fromCache('dashboard_week');

            if (hasCachedCycles && hasCachedStats && hasCachedWeek) {
                await Promise.all([loadCycles(), loadStats(), loadSavedCards()]);
                setLoading(false);
            } else {
                setLoading(true);
                await Promise.all([loadCycles(), loadStats(), loadSavedCards()]);
                setLoading(false);
            }
        };
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRemoveSavedCard = async (savedCardId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const card = savedCards.find(c => c._id === savedCardId);
            if (!card) return;
            const res = await fetch(
                `${API_BASE_URL}/saved-cards/${card.deckId}/${card.subdeckId}/${card.cardId}`,
                { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.ok) {
                setSavedCards(cards => cards.filter(c => c._id !== savedCardId));
                showToast('Flashcard removido dos salvos', 'success');
            }
        } catch { showToast('Erro ao remover flashcard', 'error'); }
    };

    const handleToggleCycleBlock = async (cycleId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/cycles/${cycleId}/toggle-block`, {
                method: 'PATCH', headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCycles(prev => prev.map(c =>
                    c._id === cycleId ? { ...c, isBlocked: data.cycle.isBlocked } : c
                ));
                showToast(data.message, 'success');
            }
        } catch { showToast('Erro ao alternar bloqueio', 'error'); }
    };

    const handleToggleCycleHide = async (cycleId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/cycles/${cycleId}/toggle-hide`, {
                method: 'PATCH', headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCycles(prev => prev.map(c =>
                    c._id === cycleId ? { ...c, isHidden: data.cycle.isHidden } : c
                ));
                showToast(data.message, 'success');
            }
        } catch { showToast('Erro ao alternar visibilidade', 'error'); }
    };

    const handleDragEnd = async (event: any) => {
        if (isSavingOrder) return;
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx    = cycles.findIndex(c => c._id === active.id);
        const newIdx    = cycles.findIndex(c => c._id === over.id);
        const reordered = [...cycles];
        reordered.splice(newIdx, 0, reordered.splice(oldIdx, 1)[0]);
        setCycles(reordered);
        setIsSavingOrder(true);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch(`${API_BASE_URL}/cycles/reorder`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ cycles: reordered.map((c, i) => ({ _id: c._id, order: i + 1 })) })
            });
            if (!res.ok) throw new Error();
            showToast('Ordem salva com sucesso', 'success');
        } catch {
            loadCycles(true);
            loadCycles();
            showToast('Não foi possível salvar a nova ordem', 'error');
        } finally { setIsSavingOrder(false); }
    };

    const handleLogout = () => { logout(); navigate('/'); };

    const openInterdictedModal = (key: string) => {
        const label = SUBTAB_LABELS[key] ?? TAB_LABELS[key] ?? key;
        setInterdictedModal({ isOpen: true, name: label });
    };

    const handlePageChange = (page: SidebarPage) => {
        if (isAdmin) { setActivePage(page); return; }

        if (isBlocked(interdictedMap, page)) { openInterdictedModal(page); return; }

        if (page === 'explore') {
            const nonCreateTab = activeExploreTab === 'create-flashcards' ? 'content' : activeExploreTab;
            if (isBlocked(interdictedMap, `explore.${nonCreateTab}`)) {
                const available = firstAvailableSubtab('explore', EXPLORE_SUBTABS, interdictedMap);
                if (available) { setActiveExploreTab(available); setActivePage(page); }
                else openInterdictedModal(page);
                return;
            }
        }

        if (page === 'stats') {
            if (isBlocked(interdictedMap, `stats.${activeStatsTab}`)) {
                const available = firstAvailableSubtab('stats', STATS_SUBTABS, interdictedMap);
                if (available) { setActiveStatsTab(available); setActivePage(page); }
                else openInterdictedModal(page);
                return;
            }
        }

        setActivePage(page);
    };

    const handleExploreTabChange = (tab: ExploreSubTab) => {
        if (isAdmin) { setActiveExploreTab(tab); return; }
        if (isBlocked(interdictedMap, `explore.${tab}`)) {
            openInterdictedModal(`explore.${tab}`); return;
        }
        setActiveExploreTab(tab);
    };

    const handleStatsTabChange = (tab: StatsSubTab) => {
        if (isAdmin) { setActiveStatsTab(tab); return; }
        if (isBlocked(interdictedMap, `stats.${tab}`)) { openInterdictedModal(`stats.${tab}`); return; }
        setActiveStatsTab(tab);
    };

    // ── Itens de navegação principal ─────────────────────────
    const mainNavItems: { id: SidebarPage; label: string; Icon: React.ElementType; locked?: boolean }[] = [
        { id: 'painel',    label: 'Painel',             Icon: LayoutDashboard },
        { id: 'explore',   label: 'Flashcards',          Icon: GraduationCap },
        { id: 'questions', label: 'Questões',            Icon: ClipboardList, locked: !hasQuestions },
        { id: 'provas',    label: 'Provas e Simulados',    Icon: Award,         locked: !hasQuestions },
        { id: 'stats',     label: 'Estatísticas',        Icon: BarChart3 },
        { id: 'planner',   label: 'Assistente Graia',    Icon: Calendar,      locked: !hasPlanner },
        { id: 'groups',    label: 'Grupos de Estudo',    Icon: Users },
    ];

    // ── Pills por seção ───────────────────────────────────────
    const explorePills: PillDef[] = [
        { id: 'content',           label: 'Campos de Estudo',     Icon: Library,  interdicted: !isAdmin && isBlocked(interdictedMap, 'explore.content') },
        { id: 'saved',             label: 'Flashcards Salvos',    Icon: Bookmark, interdicted: !isAdmin && isBlocked(interdictedMap, 'explore.saved') },
        { id: 'custom',            label: 'Estudo Personalizado', Icon: Shuffle,  interdicted: !isAdmin && isBlocked(interdictedMap, 'explore.custom') },
        { id: 'create-flashcards', label: 'Criar Flashcards',     Icon: PenLine },
    ];

    const EXPLORE_TAB_TITLES: Record<string, string> = {
        content:            'Flashcards',
        saved:              'Flashcards Salvos',
        custom:             'Estudo Personalizado',
        'create-flashcards': 'Criar Flashcards',
    };

    const EXPLORE_TAB_SUBTITLES: Record<string, string> = {
        content:            'Organize seus campos de estudo e acesse seus decks',
        saved:              'Seus flashcards salvos, organizados em pastas',
        custom:             'Monte sua própria sessão combinando conteúdos livremente',
        'create-flashcards': 'Crie seus próprios flashcards personalizados',
    };

    const PROVAS_SECTION_TITLES: Record<string, string> = {
        'provas-catalogo':     'Catálogo de Provas',
        'provas-historico':    'Histórico de Provas',
        'simulados-criar':     'Criar Simulado',
        'simulados-historico': 'Histórico de Simulados',
    };

    const PROVAS_SECTION_SUBTITLES: Record<string, string> = {
        'provas-catalogo':     'Provas do ENEM e vestibulares para você resolver',
        'provas-historico':    'Suas tentativas anteriores nas provas',
        'simulados-criar':     'Monte um simulado personalizado com filtros por assunto e dificuldade',
        'simulados-historico': 'Seus simulados criados e seus resultados',
    };

    const handleStatsPill = (id: string) => {
        if (!isAdmin && statsFlashcardsOnly && id === 'questions' && !isBlocked(interdictedMap, 'stats.questions')) {
            setUpgradeModal({ open: true, featureName: 'Questões' });
            return;
        }
        handleStatsTabChange(id as StatsSubTab);
    };

    // ─────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="dashboard-page">
                <div className="loading-container">
                    <div className="spinner" />
                    <p>Carregando...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-page">

            {/* ── TOPNAV ─────────────────────────────────────────── */}
            <header className="topnav">
                <div className="topnav-inner">

                    {/* Esquerda: hambúrguer + logo + nome */}
                    <div className="topnav-brand">
                        <button
                            className="topnav-hamburger"
                            onClick={() => setMobileNavOpen(true)}
                            aria-label="Abrir menu"
                        >
                            <Menu size={22} />
                        </button>
                        <img src="/graia-logo-azul.png" alt="GRAIA" className="topnav-logo" />
                        <span className="topnav-brand-text">GRAIA</span>
                    </div>

                    {/* Centro: itens de navegação */}
                    <nav className="topnav-nav">
                        {mainNavItems.map(({ id, label, Icon, locked }) => (
                            <button
                                key={id}
                                className={[
                                    'topnav-item',
                                    activePage === id ? 'active' : '',
                                    locked ? 'locked' : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => handlePageChange(id)}
                            >
                                <Icon size={15} />
                                <span>{label}</span>
                                {locked && <Lock size={11} className="topnav-lock" />}
                            </button>
                        ))}
                    </nav>

                    {/* Direita: tema, notificações, perfil */}
                    <div className="topnav-right">
                        <button
                            className="topnav-icon-btn"
                            onClick={toggleTheme}
                            title={theme === 'light' ? 'Ativar modo escuro' : 'Ativar modo claro'}
                        >
                            {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                        </button>
                        <NotificationBell />
                        <div className="profile-dropdown-container">
                            <button className="profile-trigger" onClick={() => setShowProfileMenu(!showProfileMenu)}>
                                {user?.profilePhoto
                                    ? <img src={user.profilePhoto || ''} alt="Avatar" className="header-avatar" />
                                    : <div className="header-avatar-placeholder">{user?.username?.charAt(0).toUpperCase()}</div>
                                }
                                <ChevronDown size={14} className="dropdown-arrow" />
                            </button>
                            {showProfileMenu && (
                                <>
                                    <div className="dropdown-overlay" onClick={() => setShowProfileMenu(false)} />
                                    <div className="profile-dropdown-menu">
                                        <button className="dropdown-item" onClick={() => { setShowProfileMenu(false); navigate('/student-area'); }}>
                                            <User size={16} /> Área do Aluno
                                        </button>
                                        {isAdmin && (
                                            <button className="dropdown-item" onClick={() => { setShowProfileMenu(false); navigate('/admin'); }}>
                                                <Settings size={16} /> Painel Admin
                                            </button>
                                        )}
                                        {isVendor && !isAdmin && (
                                            <button className="dropdown-item" onClick={() => { setShowProfileMenu(false); navigate('/vendor'); }}>
                                                <Settings size={16} /> Painel Vendas
                                            </button>
                                        )}
                                        <button className="dropdown-item danger" onClick={() => { setShowProfileMenu(false); handleLogout(); }}>
                                            <LogOut size={16} /> Sair
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* ── NAV MOBILE OVERLAY ─────────────────────────────── */}
            {mobileNavOpen && (
                <div className="mobile-nav-overlay" onClick={() => setMobileNavOpen(false)}>
                    <nav className="mobile-nav-panel" onClick={e => e.stopPropagation()}>
                        <div className="mobile-nav-header">
                            <img src="/graia-logo-azul.png" alt="GRAIA" className="mobile-nav-logo" />
                            <button className="mobile-nav-close" onClick={() => setMobileNavOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="mobile-nav-items">
                            {mainNavItems.map(({ id, label, Icon, locked }) => (
                                <button
                                    key={id}
                                    className={[
                                        'mobile-nav-item',
                                        activePage === id ? 'active' : '',
                                        locked ? 'locked' : '',
                                    ].filter(Boolean).join(' ')}
                                    onClick={() => handlePageChange(id)}
                                >
                                    <Icon size={20} />
                                    <span>{label}</span>
                                    {locked && <Lock size={13} className="mobile-nav-lock" />}
                                </button>
                            ))}
                        </div>
                        {(isAdmin || (isVendor && !isAdmin)) && (
                            <div className="mobile-nav-footer">
                                {isAdmin && (
                                    <button className="mobile-nav-admin-btn" onClick={() => { navigate('/admin'); setMobileNavOpen(false); }}>
                                        <Settings size={16} /> Admin
                                    </button>
                                )}
                                {isVendor && !isAdmin && (
                                    <button className="mobile-nav-admin-btn" onClick={() => { navigate('/vendor'); setMobileNavOpen(false); }}>
                                        <Settings size={16} /> Vendas
                                    </button>
                                )}
                            </div>
                        )}
                    </nav>
                </div>
            )}

            {/* ── CONTEÚDO PRINCIPAL ─────────────────────────────── */}
            <main className="dashboard-main-content">

                {/* Painel */}
                <div style={{ display: activePage === 'painel' ? 'block' : 'none' }}>
                    <Painel
                        onNavigate={(page, subTab) => {
                            handlePageChange(page as SidebarPage);
                            if (subTab) {
                                if (page === 'stats') handleStatsTabChange(subTab as StatsSubTab);
                            }
                        }}
                        onShowToast={showToast}
                    />
                </div>

                {/* Flashcards */}
                <div style={{ display: activePage === 'explore' ? 'block' : 'none' }}>
                    <div className="section-page-header">
                        <div className="section-page-title-wrap">
                            <h2 className="section-page-title">
                                {EXPLORE_TAB_TITLES[activeExploreTab]}
                            </h2>
                            <p className="section-page-subtitle">
                                {EXPLORE_TAB_SUBTITLES[activeExploreTab]}
                            </p>
                        </div>
                        {activeExploreTab === 'content' && isAdmin && (
                            <div className="section-page-settings-wrap" ref={contentAdminMenuRef}>
                                <button
                                    className="section-page-settings-btn"
                                    onClick={() => setShowContentAdminMenu(v => !v)}
                                    title="Configurações"
                                >
                                    <Settings size={18} />
                                </button>
                                {showContentAdminMenu && (
                                    <div className="section-page-settings-menu">
                                        <button onClick={() => { setShowContentAdminMenu(false); exploreCreateCycleRef.current?.(); }}>
                                            <Plus size={14} /> Criar campo
                                        </button>
                                        <button onClick={() => { setShowContentAdminMenu(false); exploreCreateAreaRef.current?.(); }}>
                                            <Plus size={14} /> Criar área
                                        </button>
                                        <button onClick={() => { setShowContentAdminMenu(false); exploreManageAreasRef.current?.(); }}>
                                            <span style={{ fontSize: 15, lineHeight: 1 }}>≡</span> Gerenciar áreas
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        {activeExploreTab === 'saved' && (
                            <button
                                className="section-page-action"
                                onClick={() => savedFoldersNewFolderRef.current?.()}
                            >
                                <Plus size={16} />Nova Pasta
                            </button>
                        )}
                        {activeExploreTab === 'create-flashcards' && (
                            <button
                                className="section-page-action"
                                onClick={() => createFlashcardsNewColRef.current?.()}
                            >
                                <Plus size={16} />Nova Coleção
                            </button>
                        )}
                    </div>
                    <div className="section-pills-wrapper">
                        <SectionPills
                            pills={explorePills}
                            active={activeExploreTab}
                            onChange={id => handleExploreTabChange(id as ExploreSubTab)}
                        />
                    </div>
                    {activeExploreTab === 'create-flashcards'
                        ? <CreateFlashcardsPage onRegisterNewCollection={fn => { createFlashcardsNewColRef.current = fn; }} />
                        : <ExploreContent
                            cycles={cycles}
                            savedCards={savedCards}
                            isAdmin={isAdmin}
                            isSavingOrder={isSavingOrder}
                            hasRealPurchases={hasRealPurchases}
                            onCyclesChange={setCycles}
                            onReloadCycles={() => loadCycles(true)}
                            onReloadSavedCards={loadSavedCards}
                            onRemoveSavedCard={handleRemoveSavedCard}
                            onToggleCycleBlock={handleToggleCycleBlock}
                            onToggleCycleHide={handleToggleCycleHide}
                            onDragEnd={handleDragEnd}
                            onShowToast={showToast}
                            activeTab={activeExploreTab as 'content' | 'saved' | 'custom'}
                            onRegisterCreateCycle={fn => { exploreCreateCycleRef.current = fn; }}
                            onRegisterCreateArea={fn => { exploreCreateAreaRef.current = fn; }}
                            onRegisterManageAreas={fn => { exploreManageAreasRef.current = fn; }}
                            onRegisterNewFolder={fn => { savedFoldersNewFolderRef.current = fn; }}
                        />
                    }
                </div>

                {/* Questões */}
                {activePage === 'questions' && (
                    <>
                        <div className="section-page-header">
                            <div className="section-page-title-wrap">
                                <h2 className="section-page-title">
                                    {questionsViewMode === 'study'    ? 'Questões'
                                   : questionsViewMode === 'notebook' ? 'Meu Caderno de Questões'
                                   :                                    'Gerenciar Questões'}
                                </h2>
                                <p className="section-page-subtitle">
                                    {questionsViewMode === 'study'    ? 'Pratique com questões de vestibulares'
                                   : questionsViewMode === 'notebook' ? 'Suas questões salvas para revisão'
                                   :                                    'Gerencie o banco de questões da plataforma'}
                                </p>
                            </div>
                            <div className="section-page-actions-group">
                                {questionsViewMode === 'notebook' && (
                                    <button
                                        className="section-page-action section-page-action--secondary"
                                        onClick={() => notebookOpenGridRef.current?.()}
                                    >
                                        <LayoutGrid size={16} /> Ver Caderno
                                    </button>
                                )}
                                <button
                                    className={`section-page-action ${questionsViewMode !== 'notebook' ? 'section-page-action--secondary' : ''}`}
                                    onClick={() => setQuestionsViewMode(v => v === 'notebook' ? 'study' : 'notebook')}
                                >
                                    {questionsViewMode === 'notebook' ? (
                                        <><X size={16} /> Fechar Caderno</>
                                    ) : (
                                        <><BookMarked size={16} /> Meu Caderno</>
                                    )}
                                </button>
                                {isAdmin && (
                                    <div className="section-page-settings-wrap">
                                        <button
                                            className={`section-page-settings-btn ${questionsViewMode === 'manage' ? 'active' : ''}`}
                                            onClick={() => setQuestionsViewMode(v => v === 'manage' ? 'study' : 'manage')}
                                            title="Gerenciar questões"
                                        >
                                            <Settings size={18} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {questionsViewMode === 'study' && (
                            <QuestionsPage
                                onShowToast={showToast}
                                onSwitchToManage={(id) => {
                                    setQuestionsManageInitId(id);
                                    setQuestionsViewMode('manage');
                                }}
                            />
                        )}
                        {questionsViewMode === 'notebook' && (
                            <MyNotebook
                                onShowToast={showToast}
                                onRegisterOpenGrid={(fn) => { notebookOpenGridRef.current = fn; }}
                            />
                        )}
                        {questionsViewMode === 'manage' && isAdmin && (
                            <QuestionManager
                                onShowToast={showToast}
                                initialEditId={questionsManageInitId}
                                onDone={() => { setQuestionsViewMode('study'); setQuestionsManageInitId(null); }}
                            />
                        )}
                    </>
                )}

                {/* Provas e Simulados */}
                {activePage === 'provas' && (
                    <>
                        <div className="section-page-header">
                            <div className="section-page-title-wrap">
                                <h2 className="section-page-title">
                                    {PROVAS_SECTION_TITLES[activeProvasSection] ?? 'Provas e Simulados'}
                                </h2>
                                <p className="section-page-subtitle">
                                    {PROVAS_SECTION_SUBTITLES[activeProvasSection] ?? ''}
                                </p>
                            </div>
                        </div>
                        <ProvasSimulados
                            onShowToast={showToast}
                            onSectionChange={setActiveProvasSection}
                        />
                    </>
                )}

                {/* Estatísticas */}
                {activePage === 'stats' && (
                    <>
                        <div className="section-page-header">
                            <div className="section-page-title-wrap">
                                <h2 className="section-page-title">Estatísticas</h2>
                                <p className="section-page-subtitle">Acompanhe sua evolução e identifique pontos de melhoria</p>
                            </div>
                            <div className="section-page-actions-group">
                                <button
                                    className={`section-page-action ${activeStatsTab !== 'flashcards' ? 'section-page-action--secondary' : ''}`}
                                    onClick={() => handleStatsPill('flashcards')}
                                >
                                    Flashcards
                                </button>
                                <button
                                    className={`section-page-action ${activeStatsTab !== 'questions' ? 'section-page-action--secondary' : ''}`}
                                    onClick={() => handleStatsPill('questions')}
                                >
                                    Questões
                                </button>
                            </div>
                        </div>
                        <Statistics
                            stats={detailedStats}
                            weekHistory={weekHistory}
                            onReload={loadStats}
                            activeTab={activeStatsTab}
                        />
                    </>
                )}

                {/* Planner */}
                <div style={{ display: activePage === 'planner' ? 'block' : 'none' }}>
                    <div className="section-page-header">
                        <div className="section-page-title-wrap">
                            <h2 className="section-page-title">Assistente Graia</h2>
                            <p className="section-page-subtitle">Organize seus estudos com planejamento inteligente</p>
                        </div>
                    </div>
                    <PlannerPage onShowToast={showToast} />
                </div>

                {/* Grupos */}
                {activePage === 'groups' && (
                    <>
                        <div className="section-page-header">
                            <div className="section-page-title-wrap">
                                <h2 className="section-page-title">Grupos de Estudo</h2>
                                <p className="section-page-subtitle">Estude em grupo e acompanhe o progresso coletivo</p>
                            </div>
                        </div>
                        <GroupsMainTab onShowToast={showToast} />
                    </>
                )}

            </main>

            {interdictedModal?.isOpen && (
                <InterdictedModal
                    isOpen={true}
                    onClose={() => setInterdictedModal(null)}
                    entityType="tab"
                    entityName={interdictedModal.name}
                />
            )}

            <UpgradePlanModal
                isOpen={upgradeModal.open}
                onClose={() => setUpgradeModal({ open: false })}
                featureName={upgradeModal.featureName}
            />

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};

export default DashboardPage;
